import * as Dialog from "@radix-ui/react-dialog";
import Button from "@/components/ui/button";

interface FormStatusChangeConfirmationModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	currentStatus: "unpublished" | "published";
	newStatus: "unpublished" | "published";
	onConfirm: () => void;
	isLoading?: boolean;
}

export default function FormStatusChangeConfirmationModal({ open, onOpenChange, currentStatus, newStatus, onConfirm, isLoading }: FormStatusChangeConfirmationModalProps) {
	const statusLabels: Record<string, string> = {
		unpublished: "Unpublished",
		published: "Published",
	};

	const isPublishing = newStatus === "published";

	const title = isPublishing ? "Publish form?" : "Unpublish form?";

	const description = isPublishing
		? "Your form will be published and visible to everyone with the link."
		: "Your form will be unpublished and no longer visible to the public. You can publish it again later, which will be available at the same link.";

	const handleConfirm = () => {
		onConfirm();
	};

	// Determine icon and colors based on action type
	const iconBgColor = isPublishing ? "bg-green-100" : "bg-amber-100";
	const iconColor = isPublishing ? "text-green-600" : "text-amber-600";
	const newStatusBorderColor = isPublishing ? "border-green-200" : "border-amber-200";
	const newStatusBgColor = isPublishing ? "bg-green-50" : "bg-amber-50";
	const newStatusTextColor = isPublishing ? "text-green-700" : "text-amber-700";

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-md p-6 modal-content">
					{/* Icon */}
					<div className={`flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full ${iconBgColor}`}>
						{isPublishing ? (
							<svg className={`w-6 h-6 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
							</svg>
						) : (
							<svg className={`w-6 h-6 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
								/>
							</svg>
						)}
					</div>

					{/* Title */}
					<Dialog.Title className="text-lg font-semibold text-gray-900 text-center mb-2">{title}</Dialog.Title>

					{/* Description */}
					<Dialog.Description className="text-sm text-tertiary text-center mb-6">{description}</Dialog.Description>

					{/* Status change indicator */}
					<div className="flex items-center justify-center gap-3 mb-6">
						<div className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
							<span className="text-sm font-medium text-gray-900">{statusLabels[currentStatus]}</span>
						</div>
						<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
						</svg>
						<div className={`px-3 py-2 rounded-lg border ${newStatusBorderColor} ${newStatusBgColor}`}>
							<span className={`text-sm font-medium ${newStatusTextColor}`}>{statusLabels[newStatus]}</span>
						</div>
					</div>

					{/* Actions */}
					<div className="flex gap-3">
						<Button variant="soft" className="flex-1" onClick={() => onOpenChange(false)} disabled={isLoading}>
							Cancel
						</Button>
						<Button className="flex-1" onClick={handleConfirm} disabled={isLoading}>
							{isLoading ? "Updating..." : isPublishing ? "Publish" : "Unpublish"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
